import { DropdownSelectionProps } from '@shared/components/DropdownButton/DropdownSelection';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import { RoleAssignment } from 'modules/CreateWorkspaceModal/types';
import { DefaultRoleTypesEnum } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents, getUserPowerLevel, isSystemUser } from 'utils/matrixUtils';
import { WORKSPACE_ROLES_EVENT_VERSION } from 'utils/workspaceRolesEvent';

import useLeaveWorkspace from './useLeaveWorkspace';

const useLeaveWorkspaceWithAssign = () => {
  const { matrixClient } = useMatrixContext();
  const { pushToast } = useToast();
  const { leaveWorkspace } = useLeaveWorkspace();
  const { workspaceId, workspaceRoles, roleAssignments } = useWorkspacesContext({ requireWorkspaceId: true });
  const [isLoading, setIsLoading] = useState(false);

  const currentUserId = matrixClient.getUserId();
  const workspaceRoom = matrixClient.getRoom(workspaceId);
  const workspaceMembers = useMemo(
    () =>
      workspaceRoom
        ?.getJoinedMembers()
        .filter(({ userId }) => userId !== currentUserId && !isSystemUser(userId))
        .sort((a, b) => {
          const aPL = getUserPowerLevel(workspaceRoom, a.userId);
          const bPL = getUserPowerLevel(workspaceRoom, b.userId);

          return bPL - aPL;
        }) ?? [],
    [workspaceRoom, currentUserId]
  );
  const [selectedMember, setSelectedMember] = useState<string | undefined>(workspaceMembers[0]?.userId);

  useEffect(() => {
    setSelectedMember(workspaceMembers[0]?.userId);
  }, [workspaceMembers]);

  const leaveWithAssign = async () => {
    const currentUserId = matrixClient.getUserId();
    const workspaceRoom = matrixClient.getRoom(workspaceId);
    if (!workspaceId || !workspaceRoom || !currentUserId) return;

    setIsLoading(true);

    try {
      const ownerRole = workspaceRoles.find(({ defaultRoleType }) => defaultRoleType === DefaultRoleTypesEnum.OWNER);
      if (!ownerRole) throw new Error('Owner role not found for workspace');
      if (!selectedMember) throw new Error('No user is selected to assign');

      const filteredAssignments = roleAssignments.filter(
        ({ matrixId }) => matrixId !== selectedMember && matrixId !== currentUserId
      );

      const updatedRoleAssignments: RoleAssignment[] = [
        ...filteredAssignments,
        { matrixId: selectedMember, roleId: ownerRole.id, assignedBy: currentUserId },
      ];

      await matrixClient.sendStateEvent(workspaceId, CustomMeedioMatrixEvents.WORKSPACE_ROLES, {
        version: WORKSPACE_ROLES_EVENT_VERSION,
        roles: workspaceRoles,
        assignments: updatedRoleAssignments,
      });

      await leaveWorkspace(workspaceId);

      logger.info('Successfully transferred ownership and left workspace', {
        workspaceId,
        previousOwnerId: currentUserId,
        newOwnerId: selectedMember,
      });
      pushToast({ variant: 'success', title: t('workspaces.left_workspace_success') });
    } catch (error) {
      logger.error('Error while transferring ownership and leaving workspace', { workspaceId, selectedMember, error });
      pushToast({ variant: 'error', title: t('something_went_wrong_try_again_later') });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const dropdownOptions: DropdownSelectionProps[] = workspaceMembers.map(({ userId, rawDisplayName, name }) => ({
    title: rawDisplayName || name || userId,
    isSelected: userId === selectedMember,
    onClick: () => setSelectedMember(userId),
  }));

  return { leaveWithAssign, dropdownOptions, isLoading, isDisabled: !selectedMember };
};

export default useLeaveWorkspaceWithAssign;
